import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getMystudioErrorMessage,
  getSafeInternalAppPath,
  useCreateProductDraftV4,
  useMystudioMyPageV4,
  useMystudioPhotoDeliveriesV4,
  useMystudioPhotoDeliveryAssetsV4,
  useMystudioProductsV4,
  useReplaceProductPhotoSelectionV4,
  useSubmitProductRequestV4,
} from "../../Common/api/v4";
import { MobileStatusBar } from "./components/MobileStatusBar";
import { ProductHeader } from "./components/ProductHeader";
import { ProductSummarySection } from "./components/ProductSummarySection";
import { ShippingInfoSection } from "./components/ShippingInfoSection";
import { SubmitBar } from "./components/SubmitBar";
import { withMobileView } from "../MobileAppShell/mobileNavigation";
import MystudioDataState from "../MystudioDataState";
import "../MobileAppShell/MobileAppShell.css";
import "./ProductPhotoSelectPage.css";

function getProductRequestReturnPath() {
  const params = new URLSearchParams(window.location.search);
  const backPoint = params.get("back_point");
  const safeBackPoint = getSafeInternalAppPath(backPoint);
  return safeBackPoint
    ? withMobileView(safeBackPoint)
    : withMobileView("/tabs/products?tab1=2");
}

function parseAssetIds(searchParams) {
  const raw = searchParams.get("asset_ids") || searchParams.get("assetIds") || "";
  return [...new Set(
    raw
      .split(",")
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isInteger(value) && value > 0),
  )];
}

function ProductRequestCompleteDialog({ requestId, onConfirm }) {
  return (
    <div className="product-request-complete-layer" role="presentation">
      <section
        className="product-request-complete-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-request-complete-title"
      >
        <div className="product-request-complete-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M6.75 12.2 10.2 15.6 17.7 8.4" />
          </svg>
        </div>
        <h2 id="product-request-complete-title">제작 신청이 접수되었습니다</h2>
        <p>접수번호 {requestId} · 제품신청 페이지에서 진행 상태를 확인해 주세요.</p>
        <button type="button" onClick={onConfirm}>확인</button>
      </section>
    </div>
  );
}

export default function ProductPhotoSelectPage({ onBack } = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("product_id") || searchParams.get("contract_product_id") || "";
  const initialAssetIds = useMemo(() => parseAssetIds(searchParams), [searchParams]);
  const productQuery = useMystudioProductsV4();
  const myPageQuery = useMystudioMyPageV4();
  const deliveryQuery = useMystudioPhotoDeliveriesV4();
  const deliveries = deliveryQuery.data?.data?.items || [];
  const [selectedDeliveryId, setSelectedDeliveryId] = useState("");
  const [assetIds, setAssetIds] = useState(initialAssetIds);
  const assetsQuery = useMystudioPhotoDeliveryAssetsV4(selectedDeliveryId);
  const createDraftMutation = useCreateProductDraftV4();
  const selectionMutation = useReplaceProductPhotoSelectionV4();
  const submitMutation = useSubmitProductRequestV4();
  const [submitError, setSubmitError] = useState("");
  const [completedRequestId, setCompletedRequestId] = useState("");

  const product = (productQuery.data?.data?.items || []).find(
    (item) => String(item.id) === String(productId),
  ) || null;
  const requiredPhotoCount = Math.max(1, Number(product?.requiredPhotoCount) || 1);
  const maxPhotoCount = Math.max(
    requiredPhotoCount,
    Number(product?.maxPhotoCount) || requiredPhotoCount,
  );
  const shippingInfo = myPageQuery.data?.data?.shippingAddress || null;
  const isSubmitting = Boolean(
    createDraftMutation.isPending
    || createDraftMutation.isLoading
    || selectionMutation.isPending
    || selectionMutation.isLoading
    || submitMutation.isPending
    || submitMutation.isLoading,
  );
  const canSubmit = Boolean(
    product
    && product.requestable !== false
    && ["not_started", "draft"].includes(product.status)
    && assetIds.length >= requiredPhotoCount
    && assetIds.length <= maxPhotoCount
    && !isSubmitting,
  );

  useEffect(() => {
    if (!selectedDeliveryId && deliveries.length > 0) {
      setSelectedDeliveryId(String(deliveries[0].id));
    }
  }, [deliveries, selectedDeliveryId]);

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    navigate(withMobileView("/tabs/products"));
  };

  const openPhotoPicker = () => {
    document.getElementById("product-photo-picker")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleDeliveryChange = (event) => {
    setSelectedDeliveryId(event.target.value);
    setAssetIds([]);
  };

  const toggleAsset = (assetId) => {
    const normalizedId = Number(assetId);
    setSubmitError("");
    setAssetIds((current) => {
      if (current.includes(normalizedId)) {
        return current.filter((id) => id !== normalizedId);
      }
      if (current.length >= maxPhotoCount) {
        setSubmitError(`사진은 최대 ${maxPhotoCount}장까지 선택할 수 있습니다.`);
        return current;
      }
      return [...current, normalizedId];
    });
  };

  const handleSubmit = async () => {
    if (!product) {
      setSubmitError("신청할 계약 상품을 확인할 수 없습니다.");
      return;
    }
    if (product.requestable === false) {
      setSubmitError("ERP에 사진 선택 수량이 설정된 계약 상품만 신청할 수 있습니다.");
      return;
    }
    if (assetIds.length === 0) {
      setSubmitError("제품에 사용할 사진을 먼저 선택해 주세요.");
      return;
    }

    setSubmitError("");
    try {
      const draftResponse = await createDraftMutation.mutateAsync({
        contractProductId: String(product.id),
        requestedPhotoCount: assetIds.length,
        metadata: { source: "mystudio-customer-app" },
      });
      const requestId = String(draftResponse.data?.id || draftResponse.data?.requestId || "");
      if (!requestId) throw new Error("제품 신청 접수번호를 확인할 수 없습니다.");

      await selectionMutation.mutateAsync({ requestId, assetIds });
      await submitMutation.mutateAsync(requestId);
      setCompletedRequestId(requestId);
    } catch (error) {
      setSubmitError(getMystudioErrorMessage(error));
    }
  };

  const loading = productQuery.isLoading || myPageQuery.isLoading;
  const queryError = productQuery.error || myPageQuery.error;

  return (
    <section className="product-photo-select-page mobile-app-frame" data-node-id="6368:6121">
      <MobileStatusBar />
      <ProductHeader onBack={handleBack} showBack />

      <main className="photo-select-scroll">
        {!productQuery.configured || !myPageQuery.configured ? (
          <MystudioDataState
            type="error"
            title="제품 신청 API가 연결되지 않았어요"
            description="MyStudio API 연결 설정을 확인해 주세요."
          />
        ) : loading ? (
          <MystudioDataState type="loading" title="제품 신청 정보를 불러오고 있어요" />
        ) : queryError ? (
          <MystudioDataState
            type="error"
            error={queryError}
            onRetry={() => {
              productQuery.refetch();
              myPageQuery.refetch();
            }}
          />
        ) : !product ? (
          <MystudioDataState
            type="empty"
            title="계약 상품을 찾을 수 없어요"
            description="제품신청 목록에서 계약 상품을 다시 선택해 주세요."
          />
        ) : product.requestable === false ? (
          <MystudioDataState
            type="empty"
            title="사진 구성이 설정되지 않은 계약 상품이에요"
            description="ERP 상세 상품에서 최소·최대 사진 수량을 설정하면 제품신청을 진행할 수 있습니다."
          />
        ) : (
          <>
            <ProductSummarySection product={product} />
            <ShippingInfoSection
              shippingInfo={shippingInfo}
              onEdit={() => navigate(withMobileView("/tabs/mypage"))}
            />
            <section className="photo-select-composition" aria-labelledby="product-photo-selection-title">
              <h2 id="product-photo-selection-title">사진 구성</h2>
              {assetIds.length > 0 ? (
                <div className="photo-select-selection-summary" role="status">
                  <strong>{assetIds.length}장 선택됨</strong>
                  <span>선택한 사진은 접수할 때 서버에서 고객 소유 여부를 다시 확인합니다.</span>
                </div>
              ) : (
                <MystudioDataState
                  type="empty"
                  compact
                  title="선택된 사진이 없어요"
                  description="사진 보관함에서 제품에 사용할 사진을 선택해 주세요."
                />
              )}
              <button className="photo-select-change-button" type="button" onClick={openPhotoPicker}>
                {assetIds.length > 0 ? "선택한 사진 확인" : "사진 선택하기"}
              </button>
            </section>
            <section
              className="photo-select-picker"
              id="product-photo-picker"
              aria-labelledby="product-photo-picker-title"
            >
              <div className="photo-select-picker__head">
                <span>
                  <h2 id="product-photo-picker-title">제품에 사용할 사진</h2>
                  <p>
                    최소 {requiredPhotoCount}장
                    {maxPhotoCount > requiredPhotoCount ? ` · 최대 ${maxPhotoCount}장` : ""}
                  </p>
                </span>
                <strong>{assetIds.length}장 선택</strong>
              </div>
              {deliveries.length > 1 ? (
                <label className="photo-select-picker__delivery">
                  촬영 선택
                  <select value={selectedDeliveryId} onChange={handleDeliveryChange}>
                    {deliveries.map((delivery) => (
                      <option value={delivery.id} key={delivery.id}>
                        {delivery.scheduleDate || "촬영"} · {delivery.photoCount}장
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {!deliveryQuery.configured ? (
                <MystudioDataState
                  type="error"
                  compact
                  title="촬영 사진 API가 연결되지 않았어요"
                  description="MyStudio API 연결 설정을 확인해 주세요."
                />
              ) : deliveryQuery.isLoading ? (
                <MystudioDataState type="loading" compact title="촬영 사진을 확인하고 있어요" />
              ) : deliveryQuery.isError ? (
                <MystudioDataState
                  type="error"
                  compact
                  error={deliveryQuery.error}
                  onRetry={() => deliveryQuery.refetch()}
                />
              ) : deliveries.length === 0 ? (
                <MystudioDataState
                  type="empty"
                  compact
                  title="선택할 수 있는 촬영 사진이 없어요"
                  description="스튜디오에서 원본사진 제공을 완료하면 선택할 수 있습니다."
                />
              ) : assetsQuery.isLoading ? (
                <MystudioDataState type="loading" compact title="사진 목록을 불러오고 있어요" />
              ) : assetsQuery.isError ? (
                <MystudioDataState
                  type="error"
                  compact
                  error={assetsQuery.error}
                  onRetry={() => assetsQuery.refetch()}
                />
              ) : assetsQuery.data?.data?.availability === "unavailable" ? (
                <MystudioDataState
                  type="empty"
                  compact
                  title="안전한 사진 보기를 준비하고 있어요"
                  description="사진 미리보기 연결이 완료되면 이 화면에서 바로 선택할 수 있습니다."
                />
              ) : (assetsQuery.data?.data?.items || []).length === 0 ? (
                <MystudioDataState type="empty" compact title="등록된 사진이 없어요" />
              ) : (
                <div className="photo-select-picker__grid">
                  {(assetsQuery.data?.data?.items || []).map((asset) => {
                    const assetId = Number(asset.id);
                    const selected = assetIds.includes(assetId);
                    return (
                      <button
                        type="button"
                        key={asset.id}
                        className={selected ? "is-selected" : ""}
                        aria-pressed={selected}
                        onClick={() => toggleAsset(assetId)}
                      >
                        <img src={asset.thumbnailUrl || asset.url} alt="" loading="lazy" />
                        <span>{selected ? "선택됨" : "선택"}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
            {submitError ? <p className="photo-select-submit-error" role="alert">{submitError}</p> : null}
          </>
        )}
      </main>

      <SubmitBar onSubmit={handleSubmit} disabled={!canSubmit} isSubmitting={isSubmitting} />
      {completedRequestId ? (
        <ProductRequestCompleteDialog
          requestId={completedRequestId}
          onConfirm={() => {
            setCompletedRequestId("");
            navigate(getProductRequestReturnPath());
          }}
        />
      ) : null}
    </section>
  );
}
